import { useState } from "react";
import { IUseNewCashAccount } from "./type";
import { ICreateCashAccountRequest } from "@portal/types/api/ICreateCashAccountRequest";
import { validateAccountNumber } from "./validate";
import useBankAccounts from "../useBankAccounts";
import { TextResources } from "@portal/assets/text/TextResources";
import usePortfolios from "../usePortfolios";
import { mutate } from "swr";
import { handleForbiddenErrorText } from "@portal/helpers/handleForbiddenError";

const useNewCashAccount: IUseNewCashAccount = () => {
  const [accountNumber, setAccountNumber] = useState("");
  const [accountNumberError, setAccountNumberError] = useState("");
  const [apiError, setApiError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showCompleteMessage, setShowCompleteMessage] = useState(false);
  const { bankAccounts } = useBankAccounts();
  const { mainBankPortfolio } = usePortfolios();

  const clearShowCompleteMessage = () => {
    setShowCompleteMessage(false);
  };

  const handleAccountNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setApiError("");
    const replaced = e.target.value.replace(/\D/g, "").substring(0, 11);
    setAccountNumber(replaced);
    if (replaced.length === 11) {
      const errorAccountNumber = validateAccountNumber(replaced);
      setAccountNumberError(errorAccountNumber);
    }
  };

  const handleClose = () => {
    setShowCompleteMessage(false);
    setAccountNumber("");
    setAccountNumberError("");
    setApiError("");
  };

  const createNewCashAccount = async (): Promise<void> => {
    setApiError("");
    setShowCompleteMessage(false);
    const errorAccountNumber = validateAccountNumber(accountNumber);
    setAccountNumberError(errorAccountNumber);
    if (errorAccountNumber) return;

    const accountNumberTrimmed = accountNumber.replaceAll(/\s/g, "").trim();
    const accountName = `${accountNumberTrimmed.substring(0, 4)}.${accountNumberTrimmed.substring(
      4,
      6,
    )}.${accountNumberTrimmed.substring(6)}`;

    if (bankAccounts?.some(ba => ba.accountNumber === accountNumberTrimmed)) {
      setAccountNumberError(TextResources.NEW_CASH_ACCOUNT_ALREADY_EXISTS_ERROR);
      return;
    }

    setLoading(true);

    const request = {
      portfolioId: mainBankPortfolio?.portfolioId,
      bankAccountNumber: accountNumberTrimmed,
      name: accountName,
      isDefault: bankAccounts?.length === 0,
    } as ICreateCashAccountRequest;

    const requestOptions = {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(request),
    };

    const url = "/api/bankAccounts/";
    const response = await fetch(url, requestOptions);

    if (!response.ok) {
      let error = TextResources.NEW_CASH_ACCOUNT_FAILED_CREATING_ACCOUNT;
      try {
        const text = await response.text();
        if (text.startsWith("422 - Unprocessable Entity - Customer is not owner of account")) {
          error = TextResources.NEW_CASH_ACCOUNT_FAILED_CUSTOMER_NOT_OWNER;
        } else {
          const handledForbiddenError = handleForbiddenErrorText(text);
          error = handledForbiddenError ?? error;
        }
      } catch {
        // no-op
      }
      setApiError(error);
      setLoading(false);
      return;
    }

    setAccountNumber("");
    setAccountNumberError("");
    setApiError("");
    setShowCompleteMessage(true);
    setLoading(false);

    mutate("/api/bankAccounts");
  };

  return {
    handleAccountNumberChange,
    createNewCashAccount,
    handleClose,
    accountNumber,
    accountNumberError,
    apiError,
    loading,
    showCompleteMessage,
    clearShowCompleteMessage,
  };
};

export default useNewCashAccount;
